import Message from "../../Message";
import { ajax } from "../../utils";
import {
  getCheckoutPaymentState,
  getStripeElementsAppearance,
  getStripePaymentElementOptions,
  showStripeSubmissionError,
  validatePasswordSetup,
} from "./checkout-utils";
import PaymentLoadingScreen from "./PaymentLoadingScreen";

const initBookNowPayLaterPaymentMode = () => {
  const paymentTimingOptions = document.querySelectorAll('[data-bnpl-payment-timing-option]');
  const paymentForm = document.getElementById('payment-form');
  const checkouta = document.getElementById('checkouta');
  const prepaySubmitBtn = document.getElementById('prepay-submit-btn');
  const paymentElementContainer = document.getElementById('payment-element-container');
  const bnplPaymentElementContainer = document.getElementById('bnpl-payment-element-container');
  const bnplPaymentElementWrapper = document.getElementById('bnpl-payment-element');
  const savePaymentInfoBlock = document.getElementById('save-payment-info-block');
  const savePaymentMethodCheckbox = document.querySelector('input[name="save_payment_for_later"]');
  const bnplConsentBlock = document.getElementById('bnpl-consent-block');
  const amountRecapBnplElts = document.querySelectorAll('.amount-recap-bnpl');
  const toHideOnBNPL = document.querySelectorAll('.to-hide-on-bnpl');
  const toShowOnBNPL = document.querySelectorAll('.to-show-on-bnpl');
  let stripe = null;
  let elements = null;
  let setupIntent = null;
  let bnplPaymentElement = null;
  let setupIntentLoading = false;
  let savePaymentInfoWasVisible = false;
  const paymentLoadingScreen = new PaymentLoadingScreen();

  if (!paymentTimingOptions.length || !paymentForm || !checkouta || !prepaySubmitBtn || !bnplPaymentElementWrapper) {
    return;
  }

  const paymentState = getCheckoutPaymentState(paymentForm);
  paymentState.setReady('book_now_pay_later', false);

  const getBillingDetails = () => {
    const organizerFirstNameInput = paymentForm.querySelector('input[name=organizer_firstname]');
    const organizerLastNameInput = paymentForm.querySelector('input[name=organizer_lastname]');
    const organizerPhoneInput = paymentForm.querySelector('input[name=organizer_phone]');
    const organizerEmailInput = paymentForm.querySelector('input[name=organizer_email]');
    const countryInput = paymentForm.querySelector('select[name=country]');
    let name = '';
    let phone = '';
    let email = '';

    if (organizerFirstNameInput && organizerLastNameInput && organizerPhoneInput && organizerEmailInput) {
      name = organizerFirstNameInput.value + ' ' + organizerLastNameInput.value;
      phone = organizerPhoneInput.value;
      email = organizerEmailInput.value;
    }
    else {
      const firstNameInput = paymentForm.querySelector('input[name=firstname]');
      const lastNameInput = paymentForm.querySelector('input[name=lastname]');
      const phoneInput = paymentForm.querySelector('input[name=phone]');
      const emailInput = paymentForm.querySelector('input[name=mail]');

      name = (firstNameInput ? firstNameInput.value : '') + ' ' + (lastNameInput ? lastNameInput.value : '');
      phone = phoneInput ? phoneInput.value : '';
      email = emailInput ? emailInput.value : '';
    }

    const billingDetails = {
      name: name,
      phone: phone,
      email: email,
    };

    if (countryInput && countryInput.value) {
      billingDetails.address = {
        country: countryInput.value,
      };
    }

    return billingDetails;
  };

  const createSetupIntent = () => {
    return new Promise(resolve => {
      if (!checkouta.dataset.bnplSetupIntentUrl) {
        resolve(null);
        return;
      }

      ajax(checkouta.dataset.bnplSetupIntentUrl, (data) => {
        if (!data.success || !data.setupIntentPayload) {
          Message.error(data.message || 'Book Now & Pay Later is not available.');
          resolve(null);
          return;
        }

        resolve(data.setupIntentPayload);
      }, 'POST', new FormData(), {}, true, true);
    });
  };

  const prepareBookNowPayLaterConfirmation = () => {
    return new Promise(resolve => {
      if (!checkouta.dataset.bnplPrepareConfirmationUrl) {
        resolve(false);
        return;
      }

      ajax(checkouta.dataset.bnplPrepareConfirmationUrl, (data) => {
        if (!data.success) {
          Message.error(data.message || 'Book Now & Pay Later confirmation failed.');
          resolve(false);
          return;
        }

        resolve(true);
      }, 'POST', new FormData(paymentForm), {}, true, true);
    });
  };

  const handleSetupIntentElementError = (error) => {
    const failedElement = bnplPaymentElement;
    elements = null;
    bnplPaymentElement = null;
    paymentState.setReady('book_now_pay_later', false);
    if (failedElement) {
      failedElement.destroy();
    }
    showStripeSubmissionError(error, { paymentType: 'book_now_pay_later' });
    if (paymentForm.dataset.paymentType === 'book_now_pay_later') {
      selectPaymentTiming('pay_now');
    }
  };

  const mountSetupIntentElement = async () => {
    if (elements && bnplPaymentElement) {
      return true;
    }

    if (setupIntentLoading) {
      return false;
    }

    setupIntentLoading = true;
    paymentState.setReady('book_now_pay_later', false);

    try {
      setupIntent = await createSetupIntent();
      if (!setupIntent) {
        if (paymentForm.dataset.paymentType === 'book_now_pay_later') {
          selectPaymentTiming('pay_now');
        }
        return false;
      }

      if (!stripe) {
        stripe = Stripe(checkouta.dataset.stripePublishKey);
      }

      const elementsOptions = {
        clientSecret: setupIntent.client_secret,
        appearance: getStripeElementsAppearance(),
      };

      if (setupIntent.customer_session_client_secret) {
        elementsOptions.customerSessionClientSecret = setupIntent.customer_session_client_secret;
      }

      elements = stripe.elements(elementsOptions);
      bnplPaymentElement = elements.create('payment', getStripePaymentElementOptions());
      const mountedElement = bnplPaymentElement;
      bnplPaymentElement.on('ready', () => {
        if (bnplPaymentElement === mountedElement) {
          paymentState.setReady('book_now_pay_later', true);
        }
      });
      bnplPaymentElement.on('loaderror', ({ error }) => {
        if (bnplPaymentElement === mountedElement) {
          handleSetupIntentElementError(error);
        }
      });
      bnplPaymentElement.mount('#bnpl-payment-element');

      return true;
    }
    catch (error) {
      handleSetupIntentElementError(error);
      return false;
    }
    finally {
      setupIntentLoading = false;
    }
  };

  const setBookNowPayLaterMode = (isBookNowPayLater) => {
    paymentForm.dataset.paymentType = isBookNowPayLater ? 'book_now_pay_later' : 'prepay';

    if (paymentElementContainer) {
      paymentElementContainer.classList.toggle('hidden', isBookNowPayLater);
    }

    if (bnplPaymentElementContainer) {
      bnplPaymentElementContainer.classList.toggle('hidden', !isBookNowPayLater);
    }

    if (savePaymentInfoBlock) {
      if (isBookNowPayLater) {
        savePaymentInfoWasVisible = !savePaymentInfoBlock.classList.contains('hidden');
        savePaymentInfoBlock.classList.add('hidden');
      }
      else if (savePaymentInfoWasVisible) {
        savePaymentInfoBlock.classList.remove('hidden');
      }
    }

    if (savePaymentMethodCheckbox && isBookNowPayLater) {
      savePaymentMethodCheckbox.checked = false;
    }

    if (bnplConsentBlock) {
      bnplConsentBlock.classList.toggle('hidden', !isBookNowPayLater);
    }

    amountRecapBnplElts.forEach(elt => {
      elt.classList.toggle('hidden', !isBookNowPayLater);
    });

    toHideOnBNPL.forEach(elt => {
      elt.classList.toggle('hidden', isBookNowPayLater);
    });

    toShowOnBNPL.forEach(elt => {
      elt.classList.toggle('hidden', !isBookNowPayLater);
    });

    if (isBookNowPayLater && prepaySubmitBtn.dataset.bnplText) {
      prepaySubmitBtn.textContent = prepaySubmitBtn.dataset.bnplText;
    }
    else if (prepaySubmitBtn.dataset.payNowText) {
      prepaySubmitBtn.textContent = prepaySubmitBtn.dataset.payNowText;
    }

    paymentState.updateButton();
  };

  const selectPaymentTiming = (value) => {
    if (paymentState.submitting) {
      return;
    }

    paymentTimingOptions.forEach(option => {
      const isSelected = option.dataset.paymentTimingValue === value;
      const radio = document.querySelector(`input[name="payment_timing"][value="${option.dataset.paymentTimingValue}"]`);

      option.classList.toggle('is-selected', isSelected);
      option.setAttribute('aria-checked', isSelected ? 'true' : 'false');

      if (radio) {
        radio.checked = isSelected;
      }
    });

    setBookNowPayLaterMode(value === 'book_now_pay_later');

    if (value === 'book_now_pay_later') {
      mountSetupIntentElement();
    }
  };

  paymentTimingOptions.forEach(option => {
    option.addEventListener('click', (e) => {
      if (paymentState.submitting) {
        e.preventDefault();
        return;
      }
      selectPaymentTiming(option.dataset.paymentTimingValue);
    });

    option.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        selectPaymentTiming(option.dataset.paymentTimingValue);
      }
    });
  });

  paymentForm.addEventListener('submit', async (e) => {
    if (paymentForm.dataset.paymentType !== 'book_now_pay_later') {
      return;
    }

    e.preventDefault();

    if (paymentState.submitting || !paymentState.ready.book_now_pay_later || !stripe || !elements) {
      return false;
    }

    if (!validatePasswordSetup(paymentForm)) {
      return false;
    }

    if (!paymentForm.checkValidity()) {
      return false;
    }

    paymentState.setSubmitting(true);

    try {
      // Collect wallet details during the click, before preparing the booking on the server.
      const { error: submitError } = await elements.submit();
      if (submitError) {
        showStripeSubmissionError(submitError);
        return false;
      }

      prepaySubmitBtn.classList.add('loading');
      paymentLoadingScreen.show({ bnpl: true });

      const isPrepared = await prepareBookNowPayLaterConfirmation();
      if (!isPrepared) {
        return false;
      }

      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: checkouta.dataset.thankYouUrl,
          payment_method_data: {
            billing_details: getBillingDetails(),
          },
        },
      });

      if (result.error) {
        showStripeSubmissionError(result.error);
      }
    }
    catch (error) {
      showStripeSubmissionError(error);
    }
    finally {
      paymentLoadingScreen.hide();
      prepaySubmitBtn.classList.remove('loading');
      paymentState.setSubmitting(false);
    }

    return false;
  });
};

export { initBookNowPayLaterPaymentMode };
